import { formatDistanceToNow } from 'date-fns'
import { Bell } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useNotifications } from '@/hooks/useNotifications'
import { cn } from '@/lib/utils'
import type { Notification } from '@/services/notificationsService'

// Avatar gradient colors (same as navbar)
const avatarColors = [
  'from-blue-400 to-blue-600',
  'from-purple-400 to-purple-600',
  'from-green-400 to-green-600',
  'from-orange-400 to-orange-600',
  'from-pink-400 to-pink-600',
  'from-indigo-400 to-indigo-600',
  'from-teal-400 to-teal-600',
  'from-red-400 to-red-600',
  'from-yellow-400 to-yellow-600',
  'from-cyan-400 to-cyan-600',
  'from-emerald-400 to-emerald-600',
  'from-violet-400 to-violet-600',
]

function NotificationItem({
  notification,
  onMarkAsRead,
}: {
  notification: Notification
  onMarkAsRead: (id: string) => void
}) {
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || ''
    const last = lastName?.charAt(0) || ''
    const initials = `${first}${last}`.toUpperCase()
    return initials || 'S'
  }

  const getAvatarColor = (email?: string) => {
    if (!email) return avatarColors[0]

    let hash = 0
    for (let i = 0; i < email.length; i++) {
      const char = email.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return avatarColors[Math.abs(hash) % avatarColors.length]
  }

  const getNotificationIcon = () => {
    const initials = getInitials(
      notification.actorFirstName,
      notification.actorLastName
    )
    const colorClass = getAvatarColor(notification.actorEmail)

    return (
      <div
        className={`w-10 h-10 rounded-full bg-gradient-to-r ${colorClass} flex items-center justify-center text-white font-medium text-sm flex-shrink-0`}
      >
        {initials}
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return formatDistanceToNow(date, { addSuffix: true })
    } catch {
      return ''
    }
  }

  return (
    <div
      className={cn(
        'flex gap-3 p-3 hover:bg-muted/50 transition-colors cursor-pointer border-b border-border last:border-0',
        !notification.read && 'bg-blue-50/50 dark:bg-blue-950/20'
      )}
      onClick={() => !notification.read && onMarkAsRead(notification.id)}
    >
      {getNotificationIcon()}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {notification.actorName}
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {notification.message}
            </p>
          </div>
          {!notification.read && (
            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {formatDate(notification.createdAt)}
        </p>
      </div>
    </div>
  )
}

export function NotificationsDropdown() {
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } =
    useNotifications()

  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 })
  const [isOpen, setIsOpen] = useState(false)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const tabs = [
    { value: 'all', label: 'All' },
    { value: 'unread', label: `Unread (${unreadCount})` },
  ]

  const unreadNotifications = notifications.filter((n) => !n.read)

  // Update indicator position when active tab changes or dropdown opens
  useEffect(() => {
    const updateIndicator = () => {
      const activeIndex = tabs.findIndex((tab) => tab.value === activeTab)
      if (activeIndex !== -1 && tabRefs.current[activeIndex]) {
        const activeElement = tabRefs.current[activeIndex]!
        const containerRect =
          activeElement.parentElement!.getBoundingClientRect()
        const elementRect = activeElement.getBoundingClientRect()

        setIndicatorStyle({
          width: elementRect.width,
          left: elementRect.left - containerRect.left,
        })
      }
    }

    // Longer delay when first opening to ensure DOM is rendered
    const timer = setTimeout(updateIndicator, isOpen ? 50 : 10)
    return () => clearTimeout(timer)
  }, [activeTab, unreadCount, isOpen])

  const displayNotifications =
    activeTab === 'all' ? notifications : unreadNotifications

  return (
    <DropdownMenu onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button className="relative w-8 h-8 rounded-full hover:bg-primary-foreground/10 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-2">
          <Bell className="w-4 h-4 text-primary-foreground" />
          {unreadCount > 0 && (
            <div className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[420px] p-0">
        <div className="border-b border-border px-4">
          <div className="flex items-center justify-between relative">
            <div className="flex gap-6 relative">
              {/* Sliding indicator */}
              <div
                className="absolute bottom-0 h-0.5 bg-[#015fff] transition-all duration-300 ease-out"
                style={{
                  width: `${indicatorStyle.width}px`,
                  left: `${indicatorStyle.left}px`,
                }}
              />
              {tabs.map((tab, index) => (
                <button
                  key={tab.value}
                  ref={(el) => {
                    tabRefs.current[index] = el
                  }}
                  onClick={() => setActiveTab(tab.value as 'all' | 'unread')}
                  className={cn(
                    'py-3 text-sm font-medium transition-colors relative',
                    activeTab === tab.value
                      ? 'text-[#015fff]'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                onClick={() => markAllAsRead()}
              >
                Mark all as read
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-[500px]">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Loading notifications...
            </div>
          ) : displayNotifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {activeTab === 'unread'
                ? 'No unread notifications'
                : 'No notifications yet'}
            </div>
          ) : (
            <div>
              {displayNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
